import React, { useState, useEffect } from 'react';
import api from '../api';
import { Shield, Clock, User, CheckCircle, XCircle, Monitor, Wifi, Activity, RefreshCw } from 'lucide-react';

export default function LoginHistory() {
  const [history, setHistory] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('history');
  const [activityLog, setActivityLog] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [historyData, sessionsData, statsData, activityData] = await Promise.all([
        api.getLoginHistory(200),
        api.getActiveSessions(),
        api.getSessionStats(),
        api.getActivityLog(100)
      ]);
      setHistory(historyData);
      setSessions(sessionsData);
      setStats(statsData);
      setActivityLog(activityData);
    } catch (err) {
      console.error('Failed to load login data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fmtDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const fmtDuration = (minutes) => {
    if (!minutes && minutes !== 0) return '';
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hrs}h ${mins}m`;
  };

  const parseDevice = (ua) => {
    if (!ua || ua === 'unknown') return 'Unknown';
    if (ua.includes('Mobile') || ua.includes('Android') || ua.includes('iPhone')) return 'Mobile';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Browser';
  };

  return (
    <div className="space-y-6" data-testid="login-history-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Shield className="h-7 w-7 text-primary" /> Login History
          </h2>
          <p className="text-muted-foreground">Monitor login activity and active sessions</p>
        </div>
        <button onClick={fetchData} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 border border-input rounded-md hover:bg-muted text-sm"
          data-testid="refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4" data-testid="stat-active-sessions">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Activity className="h-4 w-4 text-green-500" /> Active Sessions
            </div>
            <p className="text-2xl font-bold">{stats.active_sessions}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4" data-testid="stat-logins-today">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <User className="h-4 w-4 text-blue-500" /> Logins Today
            </div>
            <p className="text-2xl font-bold">{stats.total_logins_today}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4" data-testid="stat-failed-today">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <XCircle className="h-4 w-4 text-red-500" /> Failed Today
            </div>
            <p className="text-2xl font-bold text-red-600">{stats.failed_logins_today}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4" data-testid="stat-logins-week">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Clock className="h-4 w-4 text-purple-500" /> Logins This Week
            </div>
            <p className="text-2xl font-bold">{stats.total_logins_week}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        <button onClick={() => setTab('history')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'history' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`} data-testid="tab-history">Login History</button>
        <button onClick={() => setTab('sessions')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'sessions' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`} data-testid="tab-sessions">Active Sessions ({sessions.length})</button>
        <button onClick={() => setTab('activity')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'activity' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`} data-testid="tab-activity">Activity Log ({activityLog.length})</button>
      </div>

      {/* Login History Table */}
      {tab === 'history' && (
        <div className="bg-card rounded-lg border border-border overflow-hidden" data-testid="history-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Date & Time</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Duration</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">IP Address</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Device</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">No login records yet</td></tr>
                ) : (
                  history.map((record, i) => (
                    <tr key={i} className="hover:bg-muted/30" data-testid={`history-row-${i}`}>
                      <td className="px-4 py-3">
                        {record.success ? (
                          <span className="flex items-center gap-1.5 text-green-600 text-sm font-medium">
                            <CheckCircle className="h-4 w-4" /> Success
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-red-600 text-sm font-medium">
                            <XCircle className="h-4 w-4" /> Failed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium">{record.email}</p>
                        {record.role && <p className="text-xs text-muted-foreground capitalize">{record.role}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{fmtDate(record.timestamp)}</td>
                      <td className="px-4 py-3">
                        {record.success && record.duration_minutes != null ? (
                          <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground" data-testid={`duration-${i}`}>
                            <Clock className="h-3.5 w-3.5" />
                            {fmtDuration(record.duration_minutes)}
                            {record.session_active && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" title="Active now" />}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Wifi className="h-3.5 w-3.5" /> {record.ip_address || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Monitor className="h-3.5 w-3.5" /> {parseDevice(record.user_agent)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{record.reason || ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Active Sessions Table */}
      {tab === 'sessions' && (
        <div className="bg-card rounded-lg border border-border overflow-hidden" data-testid="sessions-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Login Time</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Session Length</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">IP Address</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Device</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sessions.length === 0 ? (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">No active sessions</td></tr>
                ) : (
                  sessions.map((session, i) => (
                    <tr key={i} className="hover:bg-muted/30" data-testid={`session-row-${i}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium">{session.full_name || session.email}</p>
                        <p className="text-xs text-muted-foreground">{session.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${
                          session.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>{session.role}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{fmtDate(session.login_at)}</td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-sm font-medium text-green-600">
                          <Clock className="h-3.5 w-3.5" /> {fmtDuration(session.session_duration_minutes)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Wifi className="h-3.5 w-3.5" /> {session.ip_address || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Monitor className="h-3.5 w-3.5" /> {parseDevice(session.user_agent)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activity Log Table */}
      {tab === 'activity' && (
        <div className="bg-card rounded-lg border border-border overflow-hidden" data-testid="activity-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Details</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Date & Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {activityLog.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">No activity recorded yet</td></tr>
                ) : (
                  activityLog.map((log, i) => (
                    <tr key={i} className="hover:bg-muted/30" data-testid={`activity-row-${i}`}>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${
                          log.action === 'create' ? 'bg-green-100 text-green-700' :
                          log.action === 'update' ? 'bg-blue-100 text-blue-700' :
                          log.action === 'cost_price_change' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{log.action.replace('_', ' ')}</span>
                      </td>
                      <td className="px-4 py-3 text-sm capitalize">{log.entity_type.replace('_', ' ')}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{log.details}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm">{log.user_email}</p>
                        <p className="text-xs text-muted-foreground capitalize">{log.user_role}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{fmtDate(log.timestamp)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
